import { answer, autoLine, canAnswer, canFix, fix, type Answer, type Fix } from './actions.js';
import { useEngineState } from './engine-state.js';
import { openSheet } from './ui.js';

const CHIPS: readonly { kind: Fix; label: string; name: string }[] = [
  { kind: 'half', label: '½ beat', name: '½ beat: move the count half a beat' },
  { kind: 'earlier', label: '−1', name: '−1: the 1 one beat earlier' },
  { kind: 'later', label: '+1', name: '+1: the 1 one beat later' },
  { kind: 'flip', label: '1↔5', name: '1↔5: swap 1 and 5' },
];

/** Four chips that fix the count, each one tap and each undoable. `½ beat` carries a dot and a caption when the half-beat choice was a close call. */
export function FixRow() {
  const s = useEngineState();
  const enabled = canFix();
  void s; // re-render when the state changes, so `enabled` is current
  return (
    <div class="fixes-wrap">
      <div class="fixes" role="group" aria-label="Fix the count">
        {CHIPS.map((c) => (
          <button key={c.kind} type="button" class="btn chip" disabled={!enabled} aria-label={c.name} onClick={() => fix(c.kind)}>
            {c.label}
            {c.kind === 'half' && s.halfBeatUnsure && <span class="unsure-dot" aria-hidden="true" data-testid="unsure-dot" />}
          </button>
        ))}
      </div>
      <p class="caption" aria-live="off">{s.halfBeatUnsure ? 'Feels off? Try ½ beat.' : ''}</p>
    </div>
  );
}

const ANSWERS: readonly { kind: Answer; label: string; name: string }[] = [
  { kind: 'yes', label: 'Yes', name: 'Yes: the count is right' },
  { kind: 'flip', label: '1↔5', name: '1↔5: swap 1 and 5' },
  { kind: 'half', label: '½ beat', name: '½ beat: move the count half a beat' },
  { kind: 'halve', label: 'Too fast', name: 'Too fast: count half as fast' },
  { kind: 'double', label: 'Too slow', name: 'Too slow: count twice as fast' },
];

/**
 * The question of an automatic count (DESIGN §11, §11.1), in place of the fix row until it is answered: the line ("Found automatically. Is 1 right?", or the honest one when "1" is not
 * likely or sure), "How do I tell?" (a small sheet with its two sentences), and Yes · 1↔5 · ½ beat · Too fast · Too slow on one row. Each is an event: saved, undoable. Too fast and Too slow stop at one level each way.
 */
export function ConfirmRow() {
  const s = useEngineState();
  const enabled = canAnswer();
  return (
    <div class="confirm" role="group" aria-label="Is the count right?" data-testid="confirm-row">
      <p class="confirm-line">
        <span data-testid="confirm-line">{autoLine(s.auto.oneSure)}</span>{' '}
        <button type="button" class="link how" aria-haspopup="dialog" onClick={() => openSheet('howto')}>How do I tell?</button>
      </p>
      <div class="answers">
        {ANSWERS.map((a) => (
          <button
            key={a.kind}
            type="button"
            class={`btn chip${a.kind === 'yes' ? ' yes' : ''}`}
            disabled={!enabled || (a.kind === 'halve' && s.auto.level <= -1) || (a.kind === 'double' && s.auto.level >= 1)}
            aria-label={a.name}
            onClick={() => answer(a.kind)}
          >
            {a.label}
          </button>
        ))}
      </div>
    </div>
  );
}
